import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  collection,
  where,
  writeBatch,
  limit
} from "../lib/firestoreClient";
import { db } from "../lib/firebase";
import { errorDev } from "../utils/logger";

const DELETE_PAGE_SIZE = 200;

// firestoreClient normalizes SDK errors into AppRequestError with an uppercased
// code (FIRESTORE_PERMISSION_DENIED) and keeps the raw SDK code in `details`.
const isPermissionDenied = (error) => {
  const code = String(error?.code || '');
  const firestoreCode = String(error?.details?.firestoreCode || '');
  return (
    code.toUpperCase().includes('PERMISSION_DENIED') ||
    firestoreCode.toUpperCase() === 'PERMISSION-DENIED'
  );
};

const userFieldCandidates = [
  'userId',
  'fromUserId',
  'toUserId',
  'buyerId',
  'sellerId',
  'raterId',
  'poster.userId',
  'seller.userId',
  'ownerId',
  'createdBy'
];

/**
 * Turns a `deleteUserAccount` result into the numbers the user is shown.
 *
 * Client rules refuse to delete some collections (shared deal history, ratings
 * that describe a counterparty, support and moderation records). Reporting only
 * `cleanup.deleted` would overstate what happened, so both counts are surfaced
 * and an incomplete deletion is stated plainly. This function performs no
 * interpretation of *which* records were kept — Firestore internals must not
 * reach the user.
 *
 * @param {{cleanup?: {deleted?: number, denied?: unknown[], failed?: unknown[]}}} [result]
 * @return {{deleted: number, retained: number, incomplete: boolean}}
 */
export function summarizeAccountDeletion(result) {
  const cleanup = result?.cleanup || {};
  const rawDeleted = Number(cleanup.deleted);
  const deleted = Number.isFinite(rawDeleted) && rawDeleted > 0 ? Math.trunc(rawDeleted) : 0;
  const retained = (Array.isArray(cleanup.denied) ? cleanup.denied.length : 0)
    + (Array.isArray(cleanup.failed) ? cleanup.failed.length : 0);

  return { deleted, retained, incomplete: retained > 0 };
}

export class SettingsService {
  static async deleteQueryInBatches(baseQuery) {
    let deletedCount = 0;

    while (true) {
      const pageQuery = query(baseQuery, limit(DELETE_PAGE_SIZE));
      const snapshot = await getDocs(pageQuery);
      if (snapshot.empty) break;

      const batch = writeBatch(db);
      snapshot.docs.forEach((item) => batch.delete(item.ref));
      await batch.commit();
      deletedCount += snapshot.size;
    }

    return deletedCount;
  }

  static async deleteSubcollectionInBatches(parentRef, subcollectionName) {
    return this.deleteQueryInBatches(
      query(collection(parentRef, subcollectionName))
    );
  }

  static async deleteOwnedDocsWithSubcollections(baseQuery, subcollections = []) {
    let deletedCount = 0;

    while (true) {
      const pageQuery = query(baseQuery, limit(DELETE_PAGE_SIZE));
      const snapshot = await getDocs(pageQuery);
      if (snapshot.empty) break;

      for (const item of snapshot.docs) {
        for (const subcollectionName of subcollections) {
          await this.deleteSubcollectionInBatches(item.ref, subcollectionName);
        }
      }

      const batch = writeBatch(db);
      snapshot.docs.forEach((item) => batch.delete(item.ref));
      await batch.commit();
      deletedCount += snapshot.size;
    }

    return deletedCount;
  }

  static async deleteFromCollectionByUserFields(collectionName, uid, fields = userFieldCandidates) {
    let deletedCount = 0;
    const denied = [];
    const failed = [];

    for (const field of fields) {
      try {
        deletedCount += await this.deleteQueryInBatches(
          query(collection(db, collectionName), where(field, '==', uid))
        );
      } catch (error) {
        const code = String(error?.details?.firestoreCode || error?.code || '');
        if (isPermissionDenied(error)) {
          denied.push({ collection: collectionName, field, code });
          errorDev(`Deletion of ${collectionName} (${field}) was denied by Firestore rules:`, error);
        } else {
          failed.push({ collection: collectionName, field, code });
          errorDev(`Deletion query for ${collectionName} (${field}) failed:`, error);
        }
      }
    }

    return { deletedCount, denied, failed };
  }

  static async deleteChatsForUser(uid) {
    let deletedCount = 0;

    while (true) {
      const chatsQuery = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', uid),
        limit(DELETE_PAGE_SIZE)
      );

      const chatsSnap = await getDocs(chatsQuery);
      if (chatsSnap.empty) break;

      for (const chatDoc of chatsSnap.docs) {
        await this.deleteSubcollectionInBatches(chatDoc.ref, 'messages');
      }

      const batch = writeBatch(db);
      chatsSnap.docs.forEach((chatDoc) => batch.delete(chatDoc.ref));
      await batch.commit();
      deletedCount += chatsSnap.size;
    }

    return deletedCount;
  }

  static async getUserSettings(uid) {
    try {
      const ref = doc(db, "users", uid);
      const snap = await getDoc(ref);
      
      if (!snap.exists()) {
        return this.getDefaultSettings();
      }
      
      const data = snap.data();
      return this.normalizeSettings(data);
    } catch (error) {
      errorDev("Error fetching settings:", error);
      throw error;
    }
  }

  static async updateUserSettings(uid, updates) {
    try {
      const ref = doc(db, "users", uid);
      
      // Handle theme compatibility - write both new and legacy fields
      if (updates.theme !== undefined) {
        updates.darkMode = updates.theme === 'dark';
      }
      
      await setDoc(ref, updates, { merge: true });
      return updates;
    } catch (error) {
      errorDev("Error updating settings:", error);
      throw error;
    }
  }

  static async deleteUserAccount(uid, currentUser) {
    try {
      const user = currentUser;
      
      // Verify user is authenticated and matches uid
      if (!user || user.uid !== uid) {
        const error = new Error('User authentication required');
        error.code = 'auth/user-mismatch';
        throw error;
      }
      
      // Delete owned products + embedded product offers
      await this.deleteOwnedDocsWithSubcollections(
        query(collection(db, 'products'), where('poster.userId', '==', uid)),
        ['offers']
      );

      // Fallback for legacy product ownership field
      await this.deleteOwnedDocsWithSubcollections(
        query(collection(db, 'products'), where('sellerId', '==', uid)),
        ['offers']
      );

      // Delete all chat threads that include this user + all messages
      await this.deleteChatsForUser(uid);

      // Delete top-level user artifacts. Firestore rules deny several client
      // deletes (offers, ratings, support tickets, impactFeed, priceSubmissions),
      // so failures are classified and reported instead of silently reported as
      // a complete deletion.
      const cleanup = { deleted: 0, denied: [], failed: [] };
      const collect = (result) => {
        cleanup.deleted += result.deletedCount;
        cleanup.denied.push(...result.denied);
        cleanup.failed.push(...result.failed);
      };

      for (const collectionName of [
        'offers',
        'notifications',
        'ratings',
        'impactFeed',
        'priceSubmissions',
        'support_tickets',
        'wishlists'
      ]) {
        collect(await this.deleteFromCollectionByUserFields(collectionName, uid));
      }

      // Delete user-scoped subcollections. The follow subcollections were
      // removed with the follow feature; the legacy rows are still cleaned up
      // defensively because documents written before the removal still exist.
      const userRef = doc(db, 'users', uid);
      await this.deleteSubcollectionInBatches(userRef, 'wishlist');

      // Delete user profile/settings document
      await writeBatch(db).delete(userRef).commit();
      
      // Delete auth user - do this AFTER Firestore cleanup
      try {
        await user.delete();
      } catch (authError) {
        // Auth deletion failed - provide helpful error
        errorDev("Failed to delete Firebase Auth user:", authError);
        
        // If it's a requires-recent-login error, re-throw with proper code
        if (authError.code === 'auth/requires-recent-login') {
          throw authError;
        }
        
        // For other errors, create a more informative error
        const wrappedError = new Error('Failed to delete account. Please try again or contact support.');
        wrappedError.code = authError.code || 'auth/delete-failed';
        wrappedError.originalError = authError;
        throw wrappedError;
      }
      
      return { ok: true, cleanup };
    } catch (error) {
      errorDev("Error deleting account:", error);
      throw error;
    }
  }

  static async exportUserData(uid) {
    try {
      const [
        userDoc,
        productsSnap,
        offersAsBuyer,
        offersAsSeller,
        notificationsToUser,
        notificationsFromUser,
        ticketsSnap,
        ratingsGiven,
        ratingsReceived,
        wishlistSnap
      ] = await Promise.all([
        getDoc(doc(db, "users", uid)),
        getDocs(query(collection(db, "products"), where("poster.userId", "==", uid))),
        getDocs(query(collection(db, "offers"), where("buyerId", "==", uid))),
        getDocs(query(collection(db, "offers"), where("sellerId", "==", uid))),
        getDocs(query(collection(db, "notifications"), where("toUserId", "==", uid))),
        getDocs(query(collection(db, "notifications"), where("fromUserId", "==", uid))),
        getDocs(query(collection(db, "support_tickets"), where("userId", "==", uid))),
        getDocs(query(collection(db, "ratings"), where("fromUserId", "==", uid))),
        getDocs(query(collection(db, "ratings"), where("toUserId", "==", uid))),
        getDocs(collection(db, "users", uid, "wishlist"))
      ]);

      const exportData = {
        exportedAt: new Date().toISOString(),
        userId: uid,
        profile: userDoc.exists() ? userDoc.data() : null,
        products: productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        offersAsBuyer: offersAsBuyer.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        offersAsSeller: offersAsSeller.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        notificationsToUser: notificationsToUser.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        notificationsFromUser: notificationsFromUser.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        supportTickets: ticketsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        ratingsGiven: ratingsGiven.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        ratingsReceived: ratingsReceived.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        wishlist: wishlistSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      };

      return exportData;
    } catch (error) {
      errorDev("Error exporting data:", error);
      throw error;
    }
  }

  static getDefaultSettings() {
    return {
      // Personalization
      defaultLocation: "",
      theme: "system",
      language: "en",
      
      // Notifications
      notificationsEnabled: true,
      emailNotifications: true,
      pushNotifications: true,
      smsNotifications: false,
      
      // Privacy
      profilePublic: true,
      showPhone: true,
      locationSharing: true,
      searchVisibility: true,
      
      // Communication
      autoTranslate: true,
      
      // Legacy compatibility
      darkMode: false
    };
  }

  static normalizeSettings(data) {
    // Handle theme normalization (legacy darkMode support)
    const storedTheme = data.theme ?? 
      (typeof data.darkMode === 'boolean' ? (data.darkMode ? 'dark' : 'light') : 'system');

    return {
      ...this.getDefaultSettings(),
      ...data,
      theme: storedTheme,
      // Ensure publicFields exists for profile compatibility
      publicFields: data.publicFields || {
        email: true,
        phone: true,
        avatar: true,
        location: true,
        bio: true
      }
    };
  }
}